import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import config from '../../config';
import { apiGet, apiGetWithBaseURL } from '../../utils/apiAuth';
import { useToast } from '../Toast';

const TestCaseEditModal = ({ show, onHide, testCase, onSave, fileName, jsCode = '', htmlCode = '', isCloning = false }) => {
  const toast = useToast();
  const [categoryToTypes, setCategoryToTypes] = useState({});
  const [categories, setCategories] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const filtersLoadedRef = useRef(false);
  const [extractedKeywords, setExtractedKeywords] = useState([]);
  const [extractedElements, setExtractedElements] = useState({
    variables: [],
    functions: [],
    selectors: [],
    events: [],
    methods: [],
    properties: []
  });
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    category: '',
    file: '',
    // Type-specific fields
    selector: '',
    selectorCustom: '',
    variable: '',
    variables: [],
    functionName: '',
    functionNameCustom: '',
    event: '',
        expectedValue: '',
        expectedOutput: '',
        input: '',
        expectedExists: true,
        expectedText: '',
        isRuntimeCandidate: false,
        keyword: '',
        keywords: [],
        keywordCustoms: [],
        keywordType: '',
    line: '',
    count: '',
    properties: [],
    // dom_runtime fields
    fixture: '',
    events: [],
    assertions: [],
    studentCode: '',
    timeout: '',
    method: '',
    // dom_interaction fields
    interactionSelector: '',
    interactionSelectorCustom: '',
    interactionEvent: 'submit',
    setValues: [],
    expectTextSelector: '',
    expectTextSelectorCustom: '',
    expectTextMatchType: 'any',
    expectTextValue: '',
    // function_behavior fields
    behavior: '',
    // object fields
    objectName: '',
    expectedProperties: '',
    expectedMethod: '',
    // condition fields
    expectedOperator: ''
  });

  const [availableTypes, setAvailableTypes] = useState([]);

  // Helper function to transform saved assertions back to form structure
  const transformAssertionsToForm = (savedAssertions) => {
    if (!Array.isArray(savedAssertions)) return [];
    return savedAssertions.map(assertion => {
      const formAssertion = {
        type: assertion.type || 'textContent',
        selector: assertion.selector || '',
        expected: '',
        isCustom: false // Will be determined by whether selector is in extractedElements
      };
      
      // Transform saved structure back to form structure
      // Check for saved format first (textContent, textContentContains, etc.)
      if (assertion.textContent !== undefined) {
        formAssertion.type = 'textContent';
        formAssertion.expected = String(assertion.textContent);
      } else if (assertion.textContentContains !== undefined) {
        formAssertion.type = 'textContentContains';
        formAssertion.expected = String(assertion.textContentContains);
      } else if (assertion.exists !== undefined) {
        formAssertion.type = 'exists';
        formAssertion.expected = assertion.exists ? 'true' : 'false';
      } else if (assertion.message !== undefined) {
        formAssertion.type = 'alert';
        formAssertion.expected = String(assertion.message);
      } else if (assertion.expected !== undefined) {
        // For property type or other types with expected field, or if already in form format
        if (assertion.type) {
          formAssertion.type = assertion.type;
        }
        formAssertion.expected = String(assertion.expected);
      }
      
      return formAssertion;
    });
  };

  // Helper function to transform saved events back to form structure
  const transformEventsToForm = (savedEvents) => {
    if (!Array.isArray(savedEvents)) return [];
    return savedEvents.map(event => ({
      selector: event.selector || '',
      type: event.type || 'click',
      value: event.value || '',
      isCustom: false // Will be determined by whether selector is in extractedElements
    }));
  };

  // Helper function to transform saved setValues back to form structure
  const transformSetValuesToForm = (savedSetValues) => {
    if (!Array.isArray(savedSetValues)) return [];
    return savedSetValues.map(sv => ({
      selector: sv.selector || '',
      value: sv.value || ''
    }));
  };

  useEffect(() => {
    const fetchFilters = async () => {
      // Only fetch if modal is shown and filters haven't been loaded yet
      if (!show || filtersLoadedRef.current) {
        return;
      }
      
      setIsLoadingFilters(true);
      try {
        const response = await apiGetWithBaseURL('/api/filters', {},{}, config.API_JS_URL);
        const result = response.data;
          
          if (result.success && result.categories) {
            const categoryMapping = {};
            const categoryList = [];
            
            result.categories.forEach(category => {
              categoryMapping[category.value] = category.types || [];
              categoryList.push({
                value: category.value,
                label: category.label
              });
            });
            
            setCategoryToTypes(categoryMapping);
            setCategories(categoryList);
            filtersLoadedRef.current = true;
          } else {
            throw new Error(result.error || 'Failed to load filters');
          }
      } catch (error) {
        toast.error('Failed to load filters from server. Please check server connection and try again.');
        setCategoryToTypes({});
        setCategories([]);
      } finally {
        setIsLoadingFilters(false);
      }
    };

    fetchFilters();
  }, [show]);

  useEffect(() => {
    // Extract elements from code (similar to HTML file)
    const elements = {
      variables: [],
      functions: [],
      selectors: [],
      events: [],
      methods: [],
      properties: [],
      keywords: []
    };

    if (jsCode) {
      // Extract variables
      const varMatches = jsCode.match(/\b(const|let|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g);
      if (varMatches) {
        elements.variables = [...new Set(varMatches.map(m => m.split(/\s+/)[1]))];
      }

      // Extract functions
      const funcMatches = jsCode.match(/\bfunction\s+([a-zA-Z_$][a-zA-Z0-9_$]*)|([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=\s*(?:\([^)]*\)|function)/g);
      if (funcMatches) {
        elements.functions = funcMatches.map(m => {
          const match = m.match(/([a-zA-Z_$][a-zA-Z0-9_$]*)/);
          return match ? match[1] : null;
        }).filter(f => f);
        elements.functions = [...new Set(elements.functions)];
      }

      // Extract events
      const eventMatches = jsCode.match(/addEventListener\s*\(["']([^"']+)["']/g);
      if (eventMatches) {
        elements.events = [...new Set(eventMatches.map(m => m.match(/["']([^"']+)["']/)[1]))];
      }

      // Extract DOM methods
      const methodPatterns = {
        'getElementById': /\.getElementById\s*\(/g,
        'querySelector': /\.querySelector\s*\(/g,
        'querySelectorAll': /\.querySelectorAll\s*\(/g,
        'getElementsByClassName': /\.getElementsByClassName\s*\(/g,
        'getElementsByTagName': /\.getElementsByTagName\s*\(/g
      };
      
      for (const [method, pattern] of Object.entries(methodPatterns)) {
        if (pattern.test(jsCode)) {
          elements.methods.push(method);
        }
      }
      elements.methods = [...new Set(elements.methods)];

      // Extract DOM properties
      const propertyPatterns = {
        'innerText': /\.innerText\s*=/g,
        'textContent': /\.textContent\s*=/g,
        'innerHTML': /\.innerHTML\s*=/g,
        'value': /\.value\s*=/g,
        'style': /\.style\./g,
        'className': /\.className\s*=/g,
        'id': /\.id\s*=/g
      };
      
      for (const [property, pattern] of Object.entries(propertyPatterns)) {
        if (pattern.test(jsCode)) {
          elements.properties.push(property);
        }
      }
      elements.properties = [...new Set(elements.properties)];

      // Extract keywords
      const keywordPatterns = {
        'const': /\bconst\s+\w+/,
        'let': /\blet\s+\w+/,
        'var': /\bvar\s+\w+/,
        'function': /\bfunction\s+(\w+)?\s*\(/,
        'if': /\bif\s*\(/,
        'else': /\belse\s*\{/,
        'for': /\bfor\s*\(/,
        'while': /\bwhile\s*\(/,
        'do': /\bdo\s*\{/,
        'switch': /\bswitch\s*\(/,
        'case': /\bcase\s+/,
        'break': /\bbreak\s*;/,  
        'continue': /\bcontinue\s*;/,
        'return': /\breturn\b/,
        'try': /\btry\s*\{/,
        'catch': /\bcatch\s*\(/,
        'finally': /\bfinally\s*\{/,
        'throw': /\bthrow\b/,
        'async': /\basync\s+(function|\(|=>)/,
        'await': /\bawait\b/,
        'class': /\bclass\s+\w+/,
        'of': /\bfor\s*\(\s*\w+\s+of\s+/,
        'in': /\bfor\s*\(\s*\w+\s+in\s+/
      };

      for (const [keyword, pattern] of Object.entries(keywordPatterns)) {
        if (pattern.test(jsCode)) {
          elements.keywords.push(keyword);
        }
      }
      elements.keywords = [...new Set(elements.keywords)];

      // Extract selectors from querySelector/querySelectorAll calls in JS
      const selectorMatches = jsCode.match(/(?:querySelector|querySelectorAll|getElementById|getElementsByClassName)\s*\(["']([^"']+)["']/g);
      if (selectorMatches) {
        const extractedSelectors = selectorMatches.map(m => {
          const match = m.match(/["']([^"']+)["']/);
          return match ? match[1] : null;
        }).filter(s => s);
        elements.selectors = [...new Set(extractedSelectors)];
      }
    }

    // Extract selectors from HTML code if available
    if (htmlCode) {
      const idMatches = htmlCode.match(/id\s*=\s*["']([^"']+)["']/g);
      const classMatches = htmlCode.match(/class\s*=\s*["']([^"']+)["']/g);
      if (idMatches) {
        const ids = [...new Set(idMatches.map(m => '#' + m.match(/["']([^"']+)["']/)[1]))];
        elements.selectors = [...new Set([...elements.selectors, ...ids])];
      }
      if (classMatches) {
        const classes = classMatches.flatMap(m => m.match(/["']([^"']+)["']/)[1].split(/\s+/)).map(c => '.' + c);
        elements.selectors = [...new Set([...elements.selectors, ...classes])];
      }
    }

    setExtractedElements(elements);
    setExtractedKeywords(elements.keywords);
  }, [jsCode, htmlCode]);

  useEffect(() => {
    if (testCase) {
      // Edit mode - populate with existing test case data
      const category = testCase.category || getCategoryForType(testCase.type);
      setFormData({
        type: testCase.type || '',
        description: testCase.description || '',
        category: category,
        file: testCase.file || fileName || '',
        selector: testCase.selector || '',
        selectorCustom: '',
        variable: testCase.variable || '',
        // For 'output' type, use expectedVariables; for 'variable' type, use variables
        variables: testCase.type === 'output' 
          ? (testCase.expectedVariables || (testCase.expectedVariable ? [testCase.expectedVariable] : []))
          : (testCase.variables || (testCase.variable ? [testCase.variable] : [])),
        functionName: testCase.functionName || '',
        functionNameCustom: '',
        event: testCase.event || '',
        expectedValue: testCase.expectedValue || '',
        expectedOutput: testCase.expectedOutput || '',
        input: testCase.input ? JSON.stringify(testCase.input) : '',
        expectedExists: testCase.expected?.exists !== undefined ? testCase.expected.exists : true,
        expectedText: testCase.expected?.text || '',
        isRuntimeCandidate: testCase ? (testCase.isRuntimeCandidate === true) : false,
        keyword: testCase.keyword || '',
        keywords: testCase.keywords ? testCase.keywords.map(k => (extractedKeywords.length > 0 && extractedKeywords.includes(k)) ? k : '') : (testCase.keyword && extractedKeywords.length > 0 && extractedKeywords.includes(testCase.keyword) ? [testCase.keyword] : []),
        keywordCustoms: testCase.keywords ? testCase.keywords.map(k => (extractedKeywords.length > 0 && extractedKeywords.includes(k)) ? '' : k) : (testCase.keyword && (extractedKeywords.length === 0 || !extractedKeywords.includes(testCase.keyword)) ? [testCase.keyword] : []),
        keywordType: testCase.keywordType || '',
        line: testCase.line || '',
        count: testCase.count || '',
        properties: testCase.properties || [],
        // dom_runtime fields
        fixture: testCase.fixture || '',
        events: transformEventsToForm(testCase.events).map(e => ({
          ...e,
          isCustom: e.selector && !extractedElements.selectors.includes(e.selector)
        })),
        assertions: transformAssertionsToForm(testCase.assertions).map(a => ({
          ...a,
          isCustom: a.selector && !extractedElements.selectors.includes(a.selector)
        })),
        studentCode: testCase.studentCode || '',
        timeout: testCase.timeout || '',
        method: testCase.method || '',
        // dom_interaction fields
        interactionSelector: testCase.selector || '',
        interactionSelectorCustom: '',
        interactionEvent: testCase.event || 'submit',
        setValues: transformSetValuesToForm(testCase.setValues),
        expectTextSelector: testCase.expectText?.selector || '',
        expectTextSelectorCustom: '',
        expectTextMatchType: testCase.expectText?.equals ? 'equals' : (testCase.expectText?.contains ? 'contains' : 'any'),
        expectTextValue: testCase.expectText?.equals || testCase.expectText?.contains || '',
        // function_behavior fields
        behavior: testCase.behavior || '',
        // object fields
        objectName: testCase.objectName || '',
        expectedProperties: Array.isArray(testCase.expectedProperties) 
          ? testCase.expectedProperties.join(', ') 
          : (testCase.expectedProperties || ''),
        expectedMethod: testCase.expectedMethod || '',
        // condition fields
        expectedOperator: testCase.expectedOperator || ''
      });
      // Set available types for the category
      if (category) {
        setAvailableTypes(categoryToTypes[category] || []);
      }
    } else {
      // Add mode - reset to default values
      setFormData({
        type: '',
        description: '',
        category: '',
        file: fileName || '',
        selector: '',
        selectorCustom: '',
        variable: '',
        variables: [],
        functionName: '',
        functionNameCustom: '',
        event: '',
        expectedValue: '',
        expectedOutput: '',
        input: '',
        expectedExists: true,
        expectedText: '',
        isRuntimeCandidate: false,
        keyword: '',
        keywords: [],
        keywordCustoms: [],
        keywordType: '',
        line: '',
        count: '',
        properties: [],
        // dom_runtime fields
        fixture: '',
        events: [],
        assertions: [],
        studentCode: '',
        timeout: '',
        method: '',
        // dom_interaction fields
        interactionSelector: '',
        interactionSelectorCustom: '',
        interactionEvent: 'submit',
        setValues: [],
        expectTextSelector: '',
        expectTextSelectorCustom: '',
        expectTextMatchType: 'any',
        expectTextValue: '',
        // function_behavior fields
        behavior: '',
        // object fields
        objectName: '',
        expectedProperties: '',
        expectedMethod: '',
        // condition fields
        expectedOperator: ''
      });
      setAvailableTypes([]);
    }
  }, [testCase, fileName, show, categoryToTypes, extractedKeywords]);

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setFormData(prev => ({
      ...prev,
      category: category,
      type: '' // Reset type when category changes
    }));
    setAvailableTypes(categoryToTypes[category] || []);
  };

  const handleChange = (e) => {
    const { name, value, type: inputType } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? e.target.checked : value
    }));
  };

  // Helper functions for managing arrays
  const addVariable = () => {
    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, '']
    }));
  };

  const removeVariable = (index) => {
    setFormData(prev => {
      const newVariables = prev.variables.filter((_, i) => i !== index);
      return {
        ...prev,
        variables: newVariables
      };
    });
  };

  const updateVariable = (index, value) => {
    setFormData(prev => {
      const newVariables = [...prev.variables];
      newVariables[index] = value;
      return { ...prev, variables: newVariables };
    });
  };

  const addKeyword = () => {
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, ''],
      keywordCustoms: [...prev.keywordCustoms, '']
    }));
  };

  const removeKeyword = (index) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index),
      keywordCustoms: prev.keywordCustoms.filter((_, i) => i !== index)
    }));
  };

  const updateKeyword = (index, value, isCustom = false) => {
    setFormData(prev => {
      const newKeywords = [...prev.keywords];
      const newKeywordCustoms = [...prev.keywordCustoms];
      if (isCustom) {
        newKeywordCustoms[index] = value;
        newKeywords[index] = '';
      } else {
        newKeywords[index] = value;
        newKeywordCustoms[index] = '';
      }
      return { ...prev, keywords: newKeywords, keywordCustoms: newKeywordCustoms };
    });
  };

  const addEvent = () => {
    setFormData(prev => ({
      ...prev,
      events: [...prev.events, { selector: '', type: 'click', value: '', isCustom: false }]
    }));
  };

  const removeEvent = (index) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.filter((_, i) => i !== index)
    }));
  };

  const updateEvent = (index, field, value) => {
    setFormData(prev => {
      const newEvents = [...prev.events];
      newEvents[index] = { ...newEvents[index], [field]: value };
      return { ...prev, events: newEvents };
    });
  };

  const addAssertion = () => {
    setFormData(prev => ({
      ...prev,
      assertions: [...prev.assertions, { selector: '', type: 'textContent', expected: '', isCustom: false }]
    }));
  };

  const removeAssertion = (index) => {
    setFormData(prev => ({
      ...prev,
      assertions: prev.assertions.filter((_, i) => i !== index)
    }));
  };

  const updateAssertion = (index, field, value) => {
    setFormData(prev => {
      const newAssertions = [...prev.assertions];
      newAssertions[index] = { ...newAssertions[index], [field]: value };
      return { ...prev, assertions: newAssertions };
    });
  };

  const addSetValue = () => {
    setFormData(prev => ({
      ...prev,
      setValues: [...prev.setValues, { selector: '', value: '' }]
    }));
  };

  const removeSetValue = (index) => {
    setFormData(prev => ({
      ...prev,
      setValues: prev.setValues.filter((_, i) => i !== index)
    }));
  };

  const updateSetValue = (index, field, value) => {
    setFormData(prev => {
      const newSetValues = [...prev.setValues];
      newSetValues[index] = { ...newSetValues[index], [field]: value };
      return { ...prev, setValues: newSetValues };
    });
  };

  const handleSave = () => {
    if (!formData.category || !formData.type || !formData.description) {
      toast.warning('Please fill in all required fields (Category, Type, and Description)');
      return;
    }

    const updatedTestCase = {
      ...testCase,
      type: formData.type,
      description: formData.description,
      category: formData.category,
      file: formData.file
    };

    // Add type-specific fields based on type
    switch(formData.type) {
      case 'variable':
        const variables = formData.variables.filter(v => v && v.trim() !== '');
        if (variables.length > 0) {
          updatedTestCase.variables = variables;
          if (variables.length === 1) {
            updatedTestCase.variable = variables[0];
          }
        }
        break;
      case 'function':
      case 'runtime_output':
        updatedTestCase.functionName = formData.functionName || formData.functionNameCustom;
        break;
      case 'html_structure':
      case 'dom_structure':
        updatedTestCase.selector = formData.selector || formData.selectorCustom;
        updatedTestCase.expected = {
          exists: formData.expectedExists
        };
        break;
      case 'dom_runtime':
        // Add method (optional)
        if (formData.method) {
          updatedTestCase.method = formData.method;
        }
        // Add selector (optional)
        if (formData.selector || formData.selectorCustom) {
          updatedTestCase.selector = formData.selector || formData.selectorCustom;
        }
        if (formData.fixture) {
          updatedTestCase.fixture = formData.fixture;
        }
        const events = formData.events.filter(e => e.selector && e.type);
        if (events.length > 0) {
          updatedTestCase.events = events;
        }
        const assertions = formData.assertions.filter(a => a.selector && a.type);
        if (assertions.length > 0) {
          updatedTestCase.assertions = assertions.map(a => {
            const assertion = {
              type: a.type,
              selector: a.selector
            };
            if (a.expected) {
              if (a.type === 'textContent') {
                assertion.textContent = a.expected;
                assertion.exact = true;
              } else if (a.type === 'textContentContains') {
                assertion.textContentContains = a.expected;
              } else if (a.type === 'property') {
                assertion.expected = a.expected;
              } else if (a.type === 'exists') {
                assertion.exists = a.expected === 'true' || a.expected === '1';
              } else if (a.type === 'alert') {
                assertion.message = a.expected;
                assertion.shouldTrigger = true;
              } else {
                assertion.expected = a.expected;
              }
            }
            return assertion;
          });
        }
        break;
      case 'dom_interaction':
        updatedTestCase.selector = formData.interactionSelector || formData.interactionSelectorCustom;
        if (formData.interactionEvent) {
          updatedTestCase.event = formData.interactionEvent;
        }
        const setValues = formData.setValues.filter(sv => sv.selector && sv.value);
        if (setValues.length > 0) {
          updatedTestCase.setValues = setValues;
        }
        if (formData.expectTextSelector) {
          updatedTestCase.expectText = {
            selector: formData.expectTextSelector || formData.expectTextSelectorCustom
          };
          if (formData.expectTextValue) {
            if (formData.expectTextMatchType === 'equals') {
              updatedTestCase.expectText.equals = formData.expectTextValue;
            } else if (formData.expectTextMatchType === 'contains') {
              updatedTestCase.expectText.contains = formData.expectTextValue;
            }
          }
        }
        break;
      case 'style':
        updatedTestCase.selector = formData.selector;
        updatedTestCase.expected = {
          exists: true
        };
        break;
      case 'event':
        updatedTestCase.event = formData.event;
        updatedTestCase.selector = formData.selector || formData.selectorCustom;
        updatedTestCase.expected = {
          listenerExists: true
        };
        break;
      case 'output':
        if (formData.expectedOutput) {
          updatedTestCase.expectedOutput = formData.expectedOutput;
        }
        // Collect expectedVariables from formData.variables
        const expectedVariables = formData.variables.filter(v => v && v.trim() !== '');
        if (expectedVariables.length > 0) {
          updatedTestCase.expectedVariables = expectedVariables;
          // Keep backward compatibility: also set single variable if only one
          if (expectedVariables.length === 1) {
            updatedTestCase.expectedVariable = expectedVariables[0];
          }
        } else {
          // Remove expectedVariables if empty
          delete updatedTestCase.expectedVariables;
          delete updatedTestCase.expectedVariable;
        }
        // Set isRuntimeCandidate flag only if checkbox is checked
        if (formData.isRuntimeCandidate) {
          updatedTestCase.isRuntimeCandidate = true;
        } else {
          delete updatedTestCase.isRuntimeCandidate;
        }
        break;
      case 'keyword':
        const keywords = formData.keywords.map((k, idx) => {
          if (k && k.trim() !== '') {
            return k.trim();
          } else if (formData.keywordCustoms[idx] && formData.keywordCustoms[idx].trim() !== '') {
            return formData.keywordCustoms[idx].trim();
          }
          return null;
        }).filter(k => k !== null);
        if (keywords.length > 0) {
          updatedTestCase.keywords = keywords;
          if (keywords.length === 1) {
            updatedTestCase.keyword = keywords[0];
          }
        }
        break;
      case 'function_behavior':
        if (formData.functionName) {
          updatedTestCase.functionName = formData.functionName || formData.functionNameCustom;
        }
        if (formData.behavior) {
          updatedTestCase.behavior = formData.behavior;
        }
        break;
      case 'document_property':
        if (formData.method) {
          updatedTestCase.method = formData.method;
        }
        if (formData.selector || formData.selectorCustom) {
          updatedTestCase.selector = formData.selector || formData.selectorCustom;
        }
        break;
      case 'object':
        if (formData.objectName) {
          updatedTestCase.objectName = formData.objectName;
        }
        if (formData.expectedProperties) {
          // Convert to array - handle both string and array inputs
          if (typeof formData.expectedProperties === 'string') {
            updatedTestCase.expectedProperties = formData.expectedProperties.split(',').map(p => p.trim()).filter(p => p);
          } else if (Array.isArray(formData.expectedProperties)) {
            updatedTestCase.expectedProperties = formData.expectedProperties.filter(p => p && p.trim && p.trim() !== '');
          } else {
            // Handle other types by converting to string first
            updatedTestCase.expectedProperties = String(formData.expectedProperties).split(',').map(p => p.trim()).filter(p => p);
          }
        }
        if (formData.expectedMethod) {
          updatedTestCase.expectedMethod = formData.expectedMethod;
        }
        break;
      case 'condition':
        if (formData.variable) {
          updatedTestCase.variable = formData.variable;
        }
        if (formData.expectedOperator) {
          updatedTestCase.expectedOperator = formData.expectedOperator;
        }
        if (formData.expectedValue) {
          updatedTestCase.expectedValue = formData.expectedValue;
        }
        break;
      case 'loop':
      case 'return':
        break;
      default:
        break;
    }

    onSave(updatedTestCase);
  };

  const getCategoryForType = (type) => {
    for (const [category, types] of Object.entries(categoryToTypes)) {
      // Handle both string array and object array formats
      const hasType = types.some(t => {
        const typeValue = typeof t === 'string' ? t : t.value;
        return typeValue === type;
      });
      if (hasType) {
        return category;
      }
    }
    return 'functional';
  };

  // Helper to get form config for a type
  const getFormConfigForType = (type) => {
    if (!categoryToTypes || !formData.category) return null;
    const types = categoryToTypes[formData.category] || [];
    const typeConfig = types.find(t => t.value === type);
    return typeConfig?.form_accessible || null;
  };

  const renderTypeSpecificFields = () => {
    if (!formData.category) {
      return (
        <div className="alert alert-info">
          <small>Please select a category first</small>
        </div>
      );
    }

    if (!formData.type) {
      return (
        <div className="alert alert-info">
          <small>Please select a type to see available fields (keys)</small>
        </div>
      );
    }

    // Get form config for this type
    const formConfig = getFormConfigForType(formData.type);
    
    // If form_accessible is not enabled or doesn't exist, show info message
    if (!formConfig || !formConfig.enable) {
      return (
        <div className="alert alert-info">
          <small>No additional fields required for this type</small>
        </div>
      );
    }

    const keys = formConfig.keys || {};
    
    // Helper functions
    const shouldShowField = (fieldName) => {
      const fieldConfig = keys[fieldName];
      return fieldConfig && fieldConfig.enable !== false;
    };
    
    const isFieldRequired = (fieldName) => {
      const fieldConfig = keys[fieldName];
      return fieldConfig && fieldConfig.required === true;
    };

    switch(formData.type) {
      case 'variable':
        return (
          <>
            {shouldShowField('variables') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Variables {isFieldRequired('variables') && <span className="text-danger">*</span>}
                  </Form.Label>
                </Form.Group>
                {formData.variables.map((variable, index) => {
                  const isCustom = variable && !extractedElements.variables.includes(variable);
                  return (
                    <div key={index} className="card mb-2">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Variable {index + 1}</strong>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeVariable(index)}
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </div>
                        <Form.Select
                          value={isCustom ? '__custom__' : (variable || '')}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              updateVariable(index, '');
                              setFormData(prev => {
                                const newVariables = [...prev.variables];
                                newVariables[index] = '';
                                return { ...prev, variables: newVariables };
                              });
                            } else {
                              updateVariable(index, e.target.value);
                            }
                          }}
                          required={isFieldRequired('variables')}
                        >
                          <option value="">Select variable...</option>
                          {extractedElements.variables.map(v => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                          <option value="__custom__">Custom...</option>
                        </Form.Select>
                        {isCustom && (
                          <Form.Control
                            type="text"
                            className="mt-1"
                            value={variable || ''}
                            onChange={(e) => updateVariable(index, e.target.value)}
                            placeholder="Enter custom variable name..."
                            required={isFieldRequired('variables')}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2 mb-3"
                  onClick={addVariable}
                >
                  <i className="fa fa-plus"></i> Add Variable
                </Button>
              </>
            )}
            {shouldShowField('expectedValue') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Value {isFieldRequired('expectedValue') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedValue') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedValue"
                  value={formData.expectedValue}
                  onChange={handleChange}
                  placeholder="e.g., 5, 'hello', true"
                  required={isFieldRequired('expectedValue')}
                />
                <small className="text-muted">Leave empty for dynamic validation (any value accepted)</small>
              </Form.Group>
            )}
          </>
        );
      
      case 'function':
      case 'runtime_output':
        return (
          <>
            {shouldShowField('functionName') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Function Name {isFieldRequired('functionName') && <span className="text-danger">*</span>}
                  {!isFieldRequired('functionName') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  value={formData.functionNameCustom ? '__custom__' : (formData.functionName || '')}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFormData(prev => ({ ...prev, functionName: '', functionNameCustom: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, functionName: e.target.value, functionNameCustom: '' }));
                    }
                  }}
                  required={isFieldRequired('functionName')}
                >
                  <option value="">Select function...</option>
                  {extractedElements.functions.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </Form.Select>
                {(formData.functionNameCustom || (formData.functionName && !extractedElements.functions.includes(formData.functionName))) && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    name="functionNameCustom"
                    value={formData.functionNameCustom || formData.functionName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, functionNameCustom: e.target.value, functionName: '' }))}
                    placeholder="Enter custom function name..."
                    required={isFieldRequired('functionName')}
                  />
                )}
              </Form.Group>
            )}
            {shouldShowField('input') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Input (JSON array) {isFieldRequired('input') && <span className="text-danger">*</span>}
                  {!isFieldRequired('input') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="input"
                  value={formData.input}
                  onChange={handleChange}
                  placeholder='e.g., [5, 10] or ["hello", "world"]'
                  required={isFieldRequired('input')}
                />
              </Form.Group>
            )}
            {shouldShowField('expectedOutput') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Output {isFieldRequired('expectedOutput') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedOutput') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedOutput"
                  value={formData.expectedOutput}
                  onChange={handleChange}
                  placeholder="Expected return value or output"
                  required={isFieldRequired('expectedOutput')}
                />
              </Form.Group>
            )}
          </>
        );
      
      case 'html_structure':
      case 'dom_structure':
        return (
          <>
            {shouldShowField('selector') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Selector {isFieldRequired('selector') && <span className="text-danger">*</span>}
                  {!isFieldRequired('selector') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  value={formData.selectorCustom ? '__custom__' : (formData.selector || '')}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFormData(prev => ({ ...prev, selector: '', selectorCustom: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, selector: e.target.value, selectorCustom: '' }));
                    }
                  }}
                  required={isFieldRequired('selector')}
                >
                  <option value="">Select selector...</option>
                  {extractedElements.selectors.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </Form.Select>
                {(formData.selectorCustom || (formData.selector && !extractedElements.selectors.includes(formData.selector))) && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    name="selectorCustom"
                    value={formData.selectorCustom || formData.selector || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, selectorCustom: e.target.value, selector: '' }))}
                    placeholder="Enter custom selector (e.g., #id, .class, tag)"
                    required={isFieldRequired('selector')}
                  />
                )}
              </Form.Group>
            )}
            {shouldShowField('expectedExists') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Exists {isFieldRequired('expectedExists') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedExists') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="expectedExists"
                  value={formData.expectedExists.toString()}
                  onChange={(e) => setFormData(prev => ({ ...prev, expectedExists: e.target.value === 'true' }))}
                  required={isFieldRequired('expectedExists')}
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Form.Select>
              </Form.Group>
            )}
            {shouldShowField('expectedText') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Text {isFieldRequired('expectedText') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedText') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedText"
                  value={formData.expectedText}
                  onChange={handleChange}
                  placeholder="Expected text content"
                  required={isFieldRequired('expectedText')}
                />
              </Form.Group>
            )}
          </>
        );

      case 'dom_runtime':
        return (
          <>
            {shouldShowField('method') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Method {isFieldRequired('method') && <span className="text-danger">*</span>}
                  {!isFieldRequired('method') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="method"
                  value={formData.method || ''}
                  onChange={handleChange}
                  required={isFieldRequired('method')}
                >
                  <option value="">Any method (not specified)</option>
                  {extractedElements.methods.length > 0 && (
                    <optgroup label="Methods found in code">
                      {extractedElements.methods.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="All methods">
                    <option value="getElementById">getElementById</option>
                    <option value="querySelector">querySelector</option>
                    <option value="querySelectorAll">querySelectorAll</option>
                    <option value="getElementsByClassName">getElementsByClassName</option>
                    <option value="getElementsByTagName">getElementsByTagName</option>
                  </optgroup>
                </Form.Select>
                <small className="text-muted">If specified, validates that this method is used. If not specified, any method is acceptable.{extractedElements.methods.length > 0 ? ` Methods found in your code: ${extractedElements.methods.join(', ')}` : ''}</small>
              </Form.Group>
            )}
            {shouldShowField('selector') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Selector {isFieldRequired('selector') && <span className="text-danger">*</span>}
                  {!isFieldRequired('selector') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  value={formData.selectorCustom ? '__custom__' : (formData.selector || '')}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFormData(prev => ({ ...prev, selector: '', selectorCustom: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, selector: e.target.value, selectorCustom: '' }));
                    }
                  }}
                  required={isFieldRequired('selector')}
                >
                  <option value="">Select selector...</option>
                  {extractedElements.selectors.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </Form.Select>
                {(formData.selectorCustom || (formData.selector && !extractedElements.selectors.includes(formData.selector))) && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    name="selectorCustom"
                    value={formData.selectorCustom || formData.selector || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, selectorCustom: e.target.value, selector: '' }))}
                    placeholder="Enter custom selector (e.g., #id, .class, tag)"
                    required={isFieldRequired('selector')}
                  />
                )}
                <small className="text-muted">Element selector to check (used with method validation)</small>
              </Form.Group>
            )}
            {shouldShowField('fixture') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Fixture HTML {isFieldRequired('fixture') && <span className="text-danger">*</span>}
                  {!isFieldRequired('fixture') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="fixture"
                  value={formData.fixture}
                  onChange={handleChange}
                  placeholder="Enter HTML fixture (e.g., <button id='btn'>Click</button>)"
                  required={isFieldRequired('fixture')}
                />
                <small className="text-muted">HTML structure for the test environment</small>
              </Form.Group>
            )}

            {shouldShowField('events') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Events {isFieldRequired('events') && <span className="text-danger">*</span>}
                    {!isFieldRequired('events') && <span className="text-muted"> (optional)</span>}
                  </Form.Label>
                </Form.Group>
            {(formData.events.length === 0 ? [{ selector: '', type: 'click', value: '', isCustom: false }] : formData.events).map((event, index) => {
              const currentEvent = formData.events.length === 0 && index === 0 
                ? { selector: '', type: 'click', value: '', isCustom: false }
                : (formData.events[index] || { selector: '', type: 'click', value: '', isCustom: false });
              const isCustomSelector = currentEvent.isCustom || (currentEvent.selector && !extractedElements.selectors.includes(currentEvent.selector));
              return (
              <div key={index} className="card mb-2">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Event {index + 1}</strong>
                    {formData.events.length > 0 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeEvent(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Form.Label>Selector</Form.Label>
                      <Form.Select
                        value={isCustomSelector ? '__custom__' : (currentEvent.selector || '')}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            if (formData.events.length === 0) {
                              setFormData(prev => ({ ...prev, events: [{ selector: '', type: 'click', value: '', isCustom: true }] }));
                            } else {
                              updateEvent(index, 'isCustom', true);
                              updateEvent(index, 'selector', '');
                            }
                          } else {
                            const value = e.target.value;
                            if (formData.events.length === 0) {
                              setFormData(prev => ({ ...prev, events: [{ selector: value, type: 'click', value: '', isCustom: false }] }));
                            } else {
                              updateEvent(index, 'selector', value);
                              updateEvent(index, 'isCustom', false);
                            }
                          }
                        }}
                      >
                        <option value="">Select...</option>
                        {extractedElements.selectors.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                        <option value="__custom__">Custom...</option>
                      </Form.Select>
                      {isCustomSelector && (
                        <Form.Control
                          type="text"
                          className="mt-1"
                          value={currentEvent.selector || ''}
                          onChange={(e) => {
                            if (formData.events.length === 0) {
                              setFormData(prev => ({ ...prev, events: [{ selector: e.target.value, type: 'click', value: '' }] }));
                            } else {
                              updateEvent(index, 'selector', e.target.value);
                            }
                          }}
                          placeholder="Custom selector"
                        />
                      )}
                    </div>
                    <div className="col-md-6 mb-2">
                      <Form.Label>Event Type</Form.Label>
                      <Form.Select
                        value={currentEvent.type || 'click'}
                        onChange={(e) => {
                          if (formData.events.length === 0) {
                            setFormData(prev => ({ ...prev, events: [{ selector: '', type: e.target.value, value: '' }] }));
                          } else {
                            updateEvent(index, 'type', e.target.value);
                          }
                        }}
                      >
                        <option value="click">click</option>
                        <option value="submit">submit</option>
                        <option value="input">input</option>
                        <option value="change">change</option>
                        <option value="focus">focus</option>
                        <option value="blur">blur</option>
                      </Form.Select>
                    </div>
                  </div>
                  {(currentEvent.type === 'input' || currentEvent.type === 'change') && (
                    <div className="row">
                      <div className="col-12 mb-2">
                        <Form.Label>Input Value</Form.Label>
                        <Form.Control
                          type="text"
                          value={currentEvent.value || ''}
                          onChange={(e) => {
                            if (formData.events.length === 0) {
                              setFormData(prev => ({ ...prev, events: [{ selector: '', type: 'click', value: e.target.value }] }));
                            } else {
                              updateEvent(index, 'value', e.target.value);
                            }
                          }}
                          placeholder="Enter value to set before triggering event"
                        />
                        <small className="text-muted">This value will be set on the element before triggering the {currentEvent.type || 'input'} event</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2 mb-3"
              onClick={addEvent}
            >
              <i className="fa fa-plus"></i> Add Event
            </Button>
              </>
            )}

            {shouldShowField('assertions') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Assertions {isFieldRequired('assertions') && <span className="text-danger">*</span>}
                    {!isFieldRequired('assertions') && <span className="text-muted"> (optional)</span>}
                  </Form.Label>
                </Form.Group>
            {(formData.assertions.length === 0 ? [{ selector: '', type: 'textContent', expected: '', isCustom: false }] : formData.assertions).map((assertion, index) => {
              const currentAssertion = formData.assertions.length === 0 && index === 0
                ? { selector: '', type: 'textContent', expected: '', isCustom: false }
                : (formData.assertions[index] || { selector: '', type: 'textContent', expected: '', isCustom: false });
              const isCustomSelector = currentAssertion.isCustom || (currentAssertion.selector && !extractedElements.selectors.includes(currentAssertion.selector));
              return (
              <div key={index} className="card mb-2">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Assertion {index + 1}</strong>
                    {formData.assertions.length > 0 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeAssertion(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <Form.Label>Type</Form.Label>
                      <Form.Select
                        value={currentAssertion.type || 'textContent'}
                        onChange={(e) => {
                          if (formData.assertions.length === 0) {
                            setFormData(prev => ({ ...prev, assertions: [{ selector: '', type: e.target.value, expected: '' }] }));
                          } else {
                            updateAssertion(index, 'type', e.target.value);
                          }
                        }}
                      >
                        <option value="textContent">textContent</option>
                        <option value="textContentContains">textContentContains</option>
                        <option value="property">property</option>
                        <option value="exists">exists</option>
                        <option value="alert">alert</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-4 mb-2">
                      <Form.Label>Selector</Form.Label>
                      <Form.Select
                        value={isCustomSelector ? '__custom__' : (currentAssertion.selector || '')}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            if (formData.assertions.length === 0) {
                              setFormData(prev => ({ ...prev, assertions: [{ selector: '', type: 'textContent', expected: '', isCustom: true }] }));
                            } else {
                              updateAssertion(index, 'isCustom', true);
                              updateAssertion(index, 'selector', '');
                            }
                          } else {
                            const value = e.target.value;
                            if (formData.assertions.length === 0) {
                              setFormData(prev => ({ ...prev, assertions: [{ selector: value, type: 'textContent', expected: '', isCustom: false }] }));
                            } else {
                              updateAssertion(index, 'selector', value);
                              updateAssertion(index, 'isCustom', false);
                            }
                          }
                        }}
                      >
                        <option value="">Select...</option>
                        {extractedElements.selectors.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                        <option value="__custom__">Custom...</option>
                      </Form.Select>
                      {isCustomSelector && (
                        <Form.Control
                          type="text"
                          className="mt-1"
                          value={currentAssertion.selector || ''}
                          onChange={(e) => {
                            if (formData.assertions.length === 0) {
                              setFormData(prev => ({ ...prev, assertions: [{ selector: e.target.value, type: 'textContent', expected: '' }] }));
                            } else {
                              updateAssertion(index, 'selector', e.target.value);
                            }
                          }}
                          placeholder="Custom selector"
                        />
                      )}
                    </div>
                    <div className="col-md-4 mb-2">
                      <Form.Label>Expected Value</Form.Label>
                      <Form.Control
                        type="text"
                        value={currentAssertion.expected || ''}
                        onChange={(e) => {
                          if (formData.assertions.length === 0) {
                            setFormData(prev => ({ ...prev, assertions: [{ selector: '', type: 'textContent', expected: e.target.value }] }));
                          } else {
                            updateAssertion(index, 'expected', e.target.value);
                          }
                        }}
                        placeholder="Expected value"
                      />
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2 mb-3"
              onClick={addAssertion}
            >
              <i className="fa fa-plus"></i> Add Assertion
            </Button>
              </>
            )}

            {shouldShowField('studentCode') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Student Code {isFieldRequired('studentCode') && <span className="text-danger">*</span>}
                  {!isFieldRequired('studentCode') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="studentCode"
                  value={formData.studentCode}
                  onChange={handleChange}
                  placeholder="JavaScript code to execute"
                  required={isFieldRequired('studentCode')}
                />
                <small className="text-muted">JavaScript code that will be executed in the test environment</small>
              </Form.Group>
            )}

            {shouldShowField('timeout') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Timeout (ms) {isFieldRequired('timeout') && <span className="text-danger">*</span>}
                  {!isFieldRequired('timeout') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="number"
                  name="timeout"
                  value={formData.timeout}
                  onChange={handleChange}
                  placeholder="1500"
                  min="100"
                  required={isFieldRequired('timeout')}
                />
              </Form.Group>
            )}
          </>
        );

      case 'style':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Selector</Form.Label>
              <Form.Control
                type="text"
                name="selector"
                value={formData.selector}
                onChange={handleChange}
                placeholder="e.g., #id, .class, tag"
              />
            </Form.Group>
          </>
        );
      
      case 'event':
        return (
          <>
            {shouldShowField('event') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Event Type {isFieldRequired('event') && <span className="text-danger">*</span>}
                  {!isFieldRequired('event') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="event"
                  value={formData.event}
                  onChange={handleChange}
                  required={isFieldRequired('event')}
                >
                  <option value="">Select event...</option>
                  {extractedElements.events.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                  <option value="click">click</option>
                  <option value="submit">submit</option>
                  <option value="change">change</option>
                  <option value="input">input</option>
                  <option value="focus">focus</option>
                  <option value="blur">blur</option>
                  <option value="load">load</option>
                  <option value="keypress">keypress</option>
                </Form.Select>
              </Form.Group>
            )}
            {shouldShowField('selector') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Element Selector {isFieldRequired('selector') && <span className="text-danger">*</span>}
                  {!isFieldRequired('selector') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  value={formData.selectorCustom ? '__custom__' : (formData.selector || '')}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFormData(prev => ({ ...prev, selector: '', selectorCustom: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, selector: e.target.value, selectorCustom: '' }));
                    }
                  }}
                  required={isFieldRequired('selector')}
                >
                  <option value="">Select selector...</option>
                  {extractedElements.selectors.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </Form.Select>
                {(formData.selectorCustom || (formData.selector && !extractedElements.selectors.includes(formData.selector))) && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    name="selectorCustom"
                    value={formData.selectorCustom || formData.selector || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, selectorCustom: e.target.value, selector: '' }))}
                    placeholder="Enter custom selector (e.g., #button, .link)"
                    required={isFieldRequired('selector')}
                  />
                )}
              </Form.Group>
            )}
          </>
        );
      
      case 'output':
        return (
          <>
            {shouldShowField('expectedOutput') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Output {isFieldRequired('expectedOutput') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedOutput') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="expectedOutput"
                  value={formData.expectedOutput}
                  onChange={handleChange}
                  placeholder="Expected console output (leave empty for dynamic validation)"
                  required={isFieldRequired('expectedOutput')}
                />
                <small className="text-muted">Leave empty to accept any console.log output. If specified, validates that the output contains this text.</small>
              </Form.Group>
            )}
            {shouldShowField('expectedVariables') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Expected Variables {isFieldRequired('expectedVariables') && <span className="text-danger">*</span>}
                    {!isFieldRequired('expectedVariables') && <span className="text-muted"> (optional)</span>}
                  </Form.Label>
                  <small className="text-muted d-block mb-2">Specify which variables should be used in console.log (prevents hardcoded values). Leave empty if no specific variables are required.</small>
                </Form.Group>
                {(formData.variables && formData.variables.length > 0 ? formData.variables : ['']).map((variable, index) => {
                  const isCustom = variable && !extractedElements.variables.includes(variable);
                  const isEmpty = !variable || variable === '';
                  return (
                    <div key={index} className="card mb-2">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Variable {index + 1}</strong>
                          {formData.variables && formData.variables.length > 0 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeVariable(index)}
                            >
                              <i className="fa fa-trash"></i>
                            </Button>
                          )}
                        </div>
                        <Form.Select
                          value={isCustom ? '__custom__' : (variable || '')}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              if (formData.variables && formData.variables.length > 0) {
                                updateVariable(index, '');
                                setFormData(prev => {
                                  const newVariables = [...prev.variables];
                                  newVariables[index] = '';
                                  return { ...prev, variables: newVariables };
                                });
                              } else {
                                setFormData(prev => ({ ...prev, variables: [''] }));
                              }
                            } else {
                              if (formData.variables && formData.variables.length > 0) {
                                updateVariable(index, e.target.value);
                              } else {
                                setFormData(prev => ({ ...prev, variables: [e.target.value] }));
                              }
                            }
                          }}
                          required={isFieldRequired('expectedVariables')}
                        >
                          <option value="">Select variable...</option>
                          {extractedElements.variables.map(v => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                          <option value="__custom__">Custom...</option>
                        </Form.Select>
                        {isCustom && (
                          <Form.Control
                            type="text"
                            className="mt-1"
                            value={variable || ''}
                            onChange={(e) => {
                              if (formData.variables && formData.variables.length > 0) {
                                updateVariable(index, e.target.value);
                              } else {
                                setFormData(prev => ({ ...prev, variables: [e.target.value] }));
                              }
                            }}
                            placeholder="Enter custom variable name..."
                            required={isFieldRequired('expectedVariables')}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2 mb-3"
                  onClick={addVariable}
                >
                  <i className="fa fa-plus"></i> Add Expected Variable
                </Button>
              </>
            )}
            <Form.Group className="mb-3 mt-3">
              <Form.Check
                type="checkbox"
                id="modalDynamicOutput"
                label="Dynamic Validation Mode"
                checked={formData.isRuntimeCandidate}
                onChange={(e) => setFormData(prev => ({ ...prev, isRuntimeCandidate: e.target.checked }))}
              />
              <small className="text-muted d-block mt-1">When enabled, accepts any console.log output without requiring specific values. Useful for requirements like "Print both membership codes" without specifying exact values.</small>
            </Form.Group>
          </>
        );

      case 'dom_interaction':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Target Element Selector</Form.Label>
              <Form.Select
                value={formData.interactionSelectorCustom ? '__custom__' : (formData.interactionSelector || '')}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setFormData(prev => ({ ...prev, interactionSelector: '', interactionSelectorCustom: '' }));
                  } else {
                    setFormData(prev => ({ ...prev, interactionSelector: e.target.value, interactionSelectorCustom: '' }));
                  }
                }}
              >
                <option value="">Select selector...</option>
                {extractedElements.selectors.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
                <option value="__custom__">Custom...</option>
              </Form.Select>
              {(formData.interactionSelectorCustom || (formData.interactionSelector && !extractedElements.selectors.includes(formData.interactionSelector))) && (
                <Form.Control
                  type="text"
                  className="mt-2"
                  name="interactionSelectorCustom"
                  value={formData.interactionSelectorCustom || formData.interactionSelector || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, interactionSelectorCustom: e.target.value, interactionSelector: '' }))}
                  placeholder="Enter custom selector (e.g., #id, .class, tag)"
                />
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Event Type</Form.Label>
              <Form.Select
                name="interactionEvent"
                value={formData.interactionEvent}
                onChange={handleChange}
              >
                <option value="submit">submit</option>
                <option value="click">click</option>
                <option value="change">change</option>
                <option value="input">input</option>
                <option value="focus">focus</option>
                <option value="blur">blur</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Set Input Values (before interaction)</Form.Label>
            </Form.Group>
            {(formData.setValues.length === 0 ? [{ selector: '', value: '' }] : formData.setValues).map((setValue, index) => {
              const currentSetValue = formData.setValues.length === 0 && index === 0
                ? { selector: '', value: '' }
                : (formData.setValues[index] || { selector: '', value: '' });
              return (
              <div key={index} className="card mb-2">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Input {index + 1}</strong>
                    {formData.setValues.length > 0 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeSetValue(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Form.Label>Selector</Form.Label>
                      <Form.Select
                        value={currentSetValue.selector && !extractedElements.selectors.includes(currentSetValue.selector) ? '__custom__' : (currentSetValue.selector || '')}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            // Keep current selector for custom input
                          } else {
                            const value = e.target.value;
                            if (formData.setValues.length === 0) {
                              setFormData(prev => ({ ...prev, setValues: [{ selector: value, value: '' }] }));
                            } else {
                              updateSetValue(index, 'selector', value);
                            }
                          }
                        }}
                      >
                        <option value="">Select...</option>
                        {extractedElements.selectors.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                        <option value="__custom__">Custom...</option>
                      </Form.Select>
                      {(currentSetValue.selector && !extractedElements.selectors.includes(currentSetValue.selector)) && (
                        <Form.Control
                          type="text"
                          className="mt-1"
                          value={currentSetValue.selector || ''}
                          onChange={(e) => {
                            if (formData.setValues.length === 0) {
                              setFormData(prev => ({ ...prev, setValues: [{ selector: e.target.value, value: '' }] }));
                            } else {
                              updateSetValue(index, 'selector', e.target.value);
                            }
                          }}
                          placeholder="Custom selector"
                        />
                      )}
                    </div>
                    <div className="col-md-6 mb-2">
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        type="text"
                        value={currentSetValue.value || ''}
                        onChange={(e) => {
                          if (formData.setValues.length === 0) {
                            setFormData(prev => ({ ...prev, setValues: [{ selector: '', value: e.target.value }] }));
                          } else {
                            updateSetValue(index, 'value', e.target.value);
                          }
                        }}
                        placeholder="Input value"
                      />
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2 mb-3"
              onClick={addSetValue}
            >
              <i className="fa fa-plus"></i> Add Input Value
            </Button>

            <Form.Group className="mb-3">
              <Form.Label>Expected Result Text</Form.Label>
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Form.Label>Result Selector</Form.Label>
                      <Form.Select
                        value={formData.expectTextSelectorCustom ? '__custom__' : (formData.expectTextSelector || '')}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setFormData(prev => ({ ...prev, expectTextSelector: '', expectTextSelectorCustom: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, expectTextSelector: e.target.value, expectTextSelectorCustom: '' }));
                          }
                        }}
                      >
                        <option value="">Select...</option>
                        {extractedElements.selectors.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                        <option value="__custom__">Custom...</option>
                      </Form.Select>
                      {(formData.expectTextSelectorCustom || (formData.expectTextSelector && !extractedElements.selectors.includes(formData.expectTextSelector))) && (
                        <Form.Control
                          type="text"
                          className="mt-1"
                          name="expectTextSelectorCustom"
                          value={formData.expectTextSelectorCustom || formData.expectTextSelector || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, expectTextSelectorCustom: e.target.value, expectTextSelector: '' }))}
                          placeholder="Custom selector"
                        />
                      )}
                    </div>
                    <div className="col-md-6 mb-2">
                      <Form.Label>Match Type</Form.Label>
                      <Form.Select
                        name="expectTextMatchType"
                        value={formData.expectTextMatchType}
                        onChange={handleChange}
                      >
                        <option value="contains">Contains</option>
                        <option value="equals">Equals (exact)</option>
                        <option value="any">Any (non-empty)</option>
                      </Form.Select>
                    </div>
                  </div>
                  <div className="mb-2">
                    <Form.Label>Expected Text Value</Form.Label>
                    <Form.Control
                      type="text"
                      name="expectTextValue"
                      value={formData.expectTextValue}
                      onChange={handleChange}
                      placeholder="Expected text"
                    />
                  </div>
                </div>
              </div>
            </Form.Group>
          </>
        );
      
      case 'keyword':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Keywords</Form.Label>
            </Form.Group>
            {(formData.keywords.length === 0 ? [''] : formData.keywords).map((keyword, index) => {
              const currentKeyword = formData.keywords.length === 0 && index === 0
                ? ''
                : (formData.keywords[index] || '');
              const currentCustom = formData.keywordCustoms[index] || '';
              const isCustom = currentCustom !== '' || (currentKeyword !== '' && !extractedKeywords.includes(currentKeyword));
              const displayValue = isCustom ? currentCustom : currentKeyword;
              
              return (
                <div key={index} className="card mb-2">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Keyword {index + 1}</strong>
                      {formData.keywords.length > 0 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeKeyword(index)}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      )}
                    </div>
                    <Form.Select
                      value={isCustom ? '__custom__' : displayValue}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          if (formData.keywords.length === 0) {
                            setFormData(prev => ({ ...prev, keywords: [''], keywordCustoms: [''] }));
                          } else {
                            updateKeyword(index, '', true);
                          }
                        } else {
                          if (formData.keywords.length === 0) {
                            setFormData(prev => ({ ...prev, keywords: [e.target.value], keywordCustoms: [''] }));
                          } else {
                            updateKeyword(index, e.target.value, false);
                          }
                        }
                      }}
                    >
                      <option value="">Select keyword...</option>
                      {extractedKeywords.map(kw => (
                        <option key={kw} value={kw}>{kw}</option>
                      ))}
                      <option value="__custom__">Custom...</option>
                    </Form.Select>
                    {isCustom && (
                      <Form.Control
                        type="text"
                        className="mt-1"
                        value={currentCustom}
                        onChange={(e) => {
                          if (formData.keywords.length === 0) {
                            setFormData(prev => ({ ...prev, keywords: [''], keywordCustoms: [e.target.value] }));
                          } else {
                            updateKeyword(index, e.target.value, true);
                          }
                        }}
                        placeholder="Enter custom keyword"
                      />
                    )}
                  </div>
                </div>
              );
            })}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2 mb-3"
              onClick={addKeyword}
            >
              <i className="fa fa-plus"></i> Add Keyword
            </Button>
          </>
        );
      
      case 'function_behavior':
        return (
          <>
            {shouldShowField('functionName') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Function Name {isFieldRequired('functionName') && <span className="text-danger">*</span>}
                  {!isFieldRequired('functionName') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="functionName"
                  value={formData.functionName}
                  onChange={handleChange}
                  placeholder="Enter function name"
                  required={isFieldRequired('functionName')}
                />
              </Form.Group>
            )}
            {shouldShowField('behavior') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Behavior {isFieldRequired('behavior') && <span className="text-danger">*</span>}
                  {!isFieldRequired('behavior') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="behavior"
                  value={formData.behavior || ''}
                  onChange={handleChange}
                  required={isFieldRequired('behavior')}
                >
                  <option value="">Select behavior...</option>
                  <option value="preventDefault">preventDefault</option>
                  <option value="stopPropagation">stopPropagation</option>
                  <option value="return">return statement</option>
                </Form.Select>
                <small className="text-muted">Specific behavior to validate within the function</small>
              </Form.Group>
            )}
          </>
        );

      case 'document_property':
        return (
          <>
            {shouldShowField('method') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Method {isFieldRequired('method') && <span className="text-danger">*</span>}
                  {!isFieldRequired('method') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="method"
                  value={formData.method || ''}
                  onChange={handleChange}
                  required={isFieldRequired('method')}
                >
                  <option value="">Select method...</option>
                  {extractedElements.methods.length > 0 && (
                    <optgroup label="Methods found in code">
                      {extractedElements.methods.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="All methods">
                    <option value="getElementById">getElementById</option>
                    <option value="querySelector">querySelector</option>
                    <option value="querySelectorAll">querySelectorAll</option>
                    <option value="getElementsByClassName">getElementsByClassName</option>
                    <option value="getElementsByTagName">getElementsByTagName</option>
                  </optgroup>
                </Form.Select>
              </Form.Group>
            )}
            {shouldShowField('selector') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Selector {isFieldRequired('selector') && <span className="text-danger">*</span>}
                  {!isFieldRequired('selector') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  value={formData.selectorCustom ? '__custom__' : (formData.selector || '')}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFormData(prev => ({ ...prev, selector: '', selectorCustom: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, selector: e.target.value, selectorCustom: '' }));
                    }
                  }}
                  required={isFieldRequired('selector')}
                >
                  <option value="">Select selector...</option>
                  {extractedElements.selectors.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </Form.Select>
                {(formData.selectorCustom || (formData.selector && !extractedElements.selectors.includes(formData.selector))) && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    name="selectorCustom"
                    value={formData.selectorCustom || formData.selector || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, selectorCustom: e.target.value, selector: '' }))}
                    placeholder="Enter custom selector (e.g., #id, .class, tag)"
                    required={isFieldRequired('selector')}
                  />
                )}
              </Form.Group>
            )}
          </>
        );

      case 'object':
        return (
          <>
            {shouldShowField('objectName') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Object Name {isFieldRequired('objectName') && <span className="text-danger">*</span>}
                  {!isFieldRequired('objectName') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="objectName"
                  value={formData.objectName || ''}
                  onChange={handleChange}
                  placeholder="Enter object name"
                  required={isFieldRequired('objectName')}
                />
              </Form.Group>
            )}
            {shouldShowField('expectedProperties') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Properties {isFieldRequired('expectedProperties') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedProperties') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedProperties"
                  value={formData.expectedProperties || ''}
                  onChange={handleChange}
                  placeholder="Comma-separated property names (e.g., name, age, email)"
                  required={isFieldRequired('expectedProperties')}
                />
                <small className="text-muted">Properties that should exist in the object</small>
              </Form.Group>
            )}
            {shouldShowField('expectedMethod') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Method {isFieldRequired('expectedMethod') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedMethod') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedMethod"
                  value={formData.expectedMethod || ''}
                  onChange={handleChange}
                  placeholder="Method name (e.g., greet, calculate)"
                  required={isFieldRequired('expectedMethod')}
                />
                <small className="text-muted">Method that should exist in the object</small>
              </Form.Group>
            )}
          </>
        );

      case 'condition':
        return (
          <>
            {shouldShowField('variable') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Variable {isFieldRequired('variable') && <span className="text-danger">*</span>}
                  {!isFieldRequired('variable') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="variable"
                  value={formData.variable}
                  onChange={handleChange}
                  placeholder="Variable name in condition"
                  required={isFieldRequired('variable')}
                />
              </Form.Group>
            )}
            {shouldShowField('expectedOperator') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Operator {isFieldRequired('expectedOperator') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedOperator') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Select
                  name="expectedOperator"
                  value={formData.expectedOperator || ''}
                  onChange={handleChange}
                  required={isFieldRequired('expectedOperator')}
                >
                  <option value="">Select operator...</option>
                  <option value="===">=== (strict equality)</option>
                  <option value="!==">!== (strict inequality)</option>
                  <option value="==">== (equality)</option>
                  <option value="!=">!= (inequality)</option>
                  <option value=">">&gt; (greater than)</option>
                  <option value="<">&lt; (less than)</option>
                  <option value=">=">&gt;= (greater than or equal)</option>
                  <option value="<=">&lt;= (less than or equal)</option>
                  <option value="&&">&& (AND)</option>
                  <option value="||">|| (OR)</option>
                </Form.Select>
              </Form.Group>
            )}
            {shouldShowField('expectedValue') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Expected Value {isFieldRequired('expectedValue') && <span className="text-danger">*</span>}
                  {!isFieldRequired('expectedValue') && <span className="text-muted"> (optional)</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="expectedValue"
                  value={formData.expectedValue}
                  onChange={handleChange}
                  placeholder="Value used in condition"
                  required={isFieldRequired('expectedValue')}
                />
              </Form.Group>
            )}
          </>
        );

      case 'loop':
      case 'return':
        return (
          <div className="alert alert-info">
            <small>No additional fields required for this type</small>
          </div>
        );
      
      default:
        return (
          <div className="alert alert-info">
            <small>No additional fields required for this type</small>
          </div>
        );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isCloning ? 'Clone Test Case' : (testCase ? 'Edit Test Case' : 'Add Custom Test Case')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleCategoryChange}
                  required
                  disabled={isLoadingFilters}
                >
                  <option value="">{isLoadingFilters ? 'Loading categories...' : 'Select category first...'}</option>
                  {categories.map(category => {
                    // Handle both string and object format
                    const categoryValue = typeof category === 'string' ? category : category.value;
                    const categoryLabel = typeof category === 'string' 
                      ? category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                      : category.label;
                    
                    return (
                      <option key={categoryValue} value={categoryValue}>
                        {categoryLabel}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled={!formData.category || availableTypes.length === 0 || isLoadingFilters}
                >
                  <option value="">
                    {isLoadingFilters ? 'Loading...' : !formData.category ? 'Select category first...' : 'Select type...'}
                  </option>
                  {availableTypes.map(type => {
                    // Handle both string array and object array formats
                    const typeValue = typeof type === 'string' ? type : type.value;
                    const typeLabel = typeof type === 'string' 
                      ? type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                      : type.label;
                    
                    return (
                      <option key={typeValue} value={typeValue}>{typeLabel}</option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Enter test case description..."
            />
          </Form.Group>

          <div className="mb-3">
            {renderTypeSpecificFields()}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Test Case
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TestCaseEditModal;
